import type { Environment } from "@backchannel/tax-contracts";

/**
 * Runtime configuration resolution order:
 *
 *   1. `window.__TAX_ENV__` — written by the container entrypoint from the
 *      Cloud Run service configuration. One immutable image, many runtimes.
 *   2. `import.meta.env.VITE_*` — build/dev-time values for local work.
 *
 * The deployed service configuration is authoritative: `environment` is never
 * inferred from a request payload, a hostname, or a stored preference.
 */
export type AppEnvConfig = {
  environment: Environment;
  apiMode: "live" | "mock";
  authMode: "platform" | "mock";
  apiBaseUrl: string;
  /**
   * Legacy unused field. Tax-owned passkeys call the Tax Admin API at
   * `apiBaseUrl`. Kept so older runtime env blobs still parse.
   */
  passkeyApiBaseUrl: string;
  mockPermissions: string[] | null;
  /** Deploy/build kill switch for session replay (`off` disables recording). */
  sessionReplay: "on" | "off";
};

type RawEnv = Record<string, string | undefined>;

declare global {
  interface Window {
    __TAX_ENV__?: RawEnv;
  }
}

const ENVIRONMENTS = new Set<Environment>(["dev", "stage", "prod"]);

const PASSKEY_API_DEFAULTS: Record<Environment, string> = {
  dev: "https://api.dev.backchannel.com.br/api/v1",
  stage: "https://api.v2.staging.backchannel.com.br/api/v1",
  prod: "https://api.v2.backchannel.com.br/api/v1",
};

function readRaw(key: string): string | undefined {
  const runtime = typeof window === "undefined" ? undefined : window.__TAX_ENV__?.[key];
  if (runtime !== undefined && runtime !== "") {
    return runtime;
  }
  const build = (import.meta.env as RawEnv)[key];
  return build === "" ? undefined : build;
}

function readString(key: string, fallback: string): string {
  return readRaw(key) ?? fallback;
}

function readEnum<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  const value = readRaw(key);
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function readList(key: string, separator: RegExp): string[] | null {
  const value = readRaw(key);
  if (!value) return null;
  const items = value
    .split(separator)
    .map((item) => item.trim())
    .filter(Boolean);
  return items.length > 0 ? items : null;
}

export function resolveEnvConfig(): AppEnvConfig {
  const environment = readEnum<Environment>(
    "VITE_TAX_ENVIRONMENT",
    [...ENVIRONMENTS] as Environment[],
    "dev",
  );

  return {
    environment,
    // A missing runtime flag must never make a deployed build look healthy
    // while silently serving fixture data. Local demos opt into mock mode via
    // `pnpm dev:mock` or an explicit environment value.
    apiMode: readEnum("VITE_TAX_API_MODE", ["live", "mock"] as const, "live"),
    authMode: readEnum("VITE_TAX_AUTH_MODE", ["platform", "mock"] as const, "mock"),
    apiBaseUrl: readString("VITE_TAX_API_BASE_URL", "/api").replace(/\/+$/, ""),
    passkeyApiBaseUrl: readString(
      "VITE_TAX_PASSKEY_API_BASE_URL",
      PASSKEY_API_DEFAULTS[environment],
    ).replace(/\/+$/, ""),
    mockPermissions: readList("VITE_TAX_MOCK_PERMISSIONS", /,/),
    sessionReplay: readEnum("VITE_TAX_SESSION_REPLAY", ["on", "off"] as const, "on"),
  };
}

/**
 * Returns the human-readable reason the platform identity flow cannot start.
 */
export function describeAuthMisconfiguration(config: AppEnvConfig): string | null {
  if (config.authMode === "mock") return null;
  if (!config.apiBaseUrl) return "VITE_TAX_API_BASE_URL";
  return null;
}

/** @deprecated Use describeAuthMisconfiguration. */
export function describeCognitoMisconfiguration(config: AppEnvConfig): string | null {
  return describeAuthMisconfiguration(config);
}

export const envConfig = resolveEnvConfig();
